/**
 * agentResearch.ts — Company research brief generator.
 *
 * Uses the JWT-authed ai-companion edge function (NOT a direct LLM call).
 * Raw Exa search results are injected as context; the LLM synthesises only
 * from those sources — NO invented facts.
 *
 * Hard rule (TASK A-9): the system prompt explicitly forbids fabrication.
 */

import { supabase } from '../../lib/supabase';
import { getAgentResponseLanguage } from './utils/agentLocale';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CompanyBriefResult {
  brief: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// System prompt
// ─────────────────────────────────────────────────────────────────────────────

const RESEARCH_SYSTEM = `You are a company intelligence analyst helping a job-seeker prepare for interviews.

You will be given LIVE WEB SEARCH RESULTS about a company. Synthesise ONLY what those results say.

CRITICAL RULES:
- Use ONLY the provided sources. Do NOT invent facts, statistics, or quotes.
- Cite inline using [1], [2], … matching the numbered sources.
- If a section has no source data, write "No recent data found."
- Output EXACTLY these 6 sections in markdown (no extra sections):

## Overview
2-3 sentences: what the company does, size, stage, notable backers/valuation.

## Recent News
2-3 sentences: latest announcements, funding rounds, product launches, or leadership changes.

## Product & Technology
2-3 sentences: core product, tech stack (if known), differentiators.

## Culture & Reviews
2-3 sentences: Glassdoor sentiment, values, remote policy, engineering culture.

## Likely Interview Topics
3 bullet points: topics commonly covered given the company's domain and tech.

## Sources
List each source as: [n] Title — URL`;

// ─────────────────────────────────────────────────────────────────────────────
// Main function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Call ai-companion with Exa search context to produce a structured company brief.
 * Returns the markdown brief string; throws on hard failure.
 */
export async function generateCompanyBrief(
  company: string,
  exaContext: string,
  signal?: AbortSignal,
): Promise<CompanyBriefResult> {
  const userContext = [
    RESEARCH_SYSTEM,
    '',
    '=== LIVE WEB SEARCH RESULTS ===',
    exaContext || 'No search results available.',
    '=== END SEARCH RESULTS ===',
  ].join('\n');

  const message = `Produce a company brief for ${company} using only the provided search results above.`;

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const { data, error } = await supabase.functions.invoke<{
    content: string;
    error?: string;
  }>('ai-companion', {
    body: {
      message,
      history:           [],
      user_context:      userContext,
      response_language: getAgentResponseLanguage(),
      assistant_gender:  'female',
    },
  });

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  if (error) {
    console.error('[agentResearch] ai-companion error:', error);
    throw new Error(error.message || 'Failed to generate company brief');
  }

  if (!data?.content) {
    throw new Error('No content returned from ai-companion');
  }

  // Strip any accidental action-tag JSON that might leak from the model
  const brief = data.content
    .replace(/\{\s*"action"\s*:\s*"[^"]+"[^}]*\}/g, '')
    .trim();

  return { brief };
}
